import React, { useRef } from 'react';
import styled from 'styled-components';
import { ImagePlus, X } from 'lucide-react';
import colors from 'theme/colors';
import fonts from 'theme/fonts';

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Foto de capa da sessão: aparece no cartão da programação, acima do horário.
 * A imagem escolhida só sobe quando a sessão é salva, então aqui só se mostra a prévia.
 */
export default function SessionImage({ url, onPick, onRemove, onError }) {
    const input = useRef(null);

    function handleChange(event) {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) {
            return;
        }

        if (!ACCEPTED.includes(file.type)) {
            onError('Use uma imagem JPG, PNG ou WebP.');
            return;
        }

        if (file.size > MAX_SIZE) {
            onError('A foto precisa ter até 10 MB.');
            return;
        }

        onPick(file, URL.createObjectURL(file));
    }

    return (
        <Wrapper>
            <Frame
                type='button'
                aria-label={url ? 'Trocar a foto da sessão' : 'Adicionar foto da sessão'}
                onClick={() => input.current?.click()}
            >
                {url ? (
                    <img src={url} alt='' />
                ) : (
                    <Empty>
                        <ImagePlus size={22} color={colors.night} />
                        <span>Adicionar foto</span>
                    </Empty>
                )}
            </Frame>

            {url && (
                <Remove type='button' aria-label='Remover foto da sessão' onClick={onRemove}>
                    <X size={12} strokeWidth={3} color={colors.neutralLight} />
                </Remove>
            )}

            <input
                ref={input}
                type='file'
                accept={ACCEPTED.join(',')}
                hidden
                onChange={handleChange}
            />
        </Wrapper>
    );
}

const Wrapper = styled.div`
    position: relative;
    width: 168px;
    height: 126px;
    margin-top: 8px;
`;

const Frame = styled.button`
    width: 100%;
    height: 100%;
    padding: 0;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    background: ${colors.neutralLight};
    border: 1px dashed ${colors.nightLight};

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &:hover { border-color: ${colors.occasionalPurple}; }
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 2px; }
`;

const Empty = styled.span`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 100%;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const Remove = styled.button`
    position: absolute;
    top: -6px;
    right: -6px;
    width: 22px;
    height: 22px;
    padding: 0;
    border-radius: 50%;
    border: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${colors.danger};
`;
